
import { supabase } from "@/integrations/supabase/client";
import { QueryResult } from "./types";

// Admin dashboard queries
export const adminQueries = {
  // User stats
  getUserStats: async (): Promise<QueryResult> => {
    const { data: userRoles, error: userError } = await supabase
      .from('user_roles')
      .select('user_id', { count: 'exact' });
    
    const { count: totalUsers } = userError ? { count: 0 } : { count: userRoles?.length || 0 };
    
    // Get weekly new users
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const { data: newUsers, error: newUsersError } = await supabase
      .from('user_roles')
      .select('created_at, user_id')
      .gte('created_at', weekAgo.toISOString());
    
    const newUsersWeek = newUsersError ? 0 : newUsers?.length || 0;
    
    return {
      data: { totalUsers, newUsersWeek },
      error: userError || newUsersError
    };
  },
  
  // Contract stats
  getContractStats: async (): Promise<QueryResult> => {
    const { data: contracts, error: contractsError } = await supabase
      .from('contracts')
      .select('*', { count: 'exact' });
    
    const { count: totalContracts } = contractsError ? { count: 0 } : { count: contracts?.length || 0 };
    
    // Get user with most contracts - Using an approach that doesn't use .group() directly
    const { data: contractsData, error: contractsDataError } = await supabase
      .from('contracts')
      .select('user_id');
      
    let userWithMostContracts = { name: "Unknown", count: 0 };
    
    if (contractsData && contractsData.length > 0) {
      // Count contracts per user
      const userContractCounts: Record<string, number> = {};
      
      contractsData.forEach(contract => {
        userContractCounts[contract.user_id] = (userContractCounts[contract.user_id] || 0) + 1;
      });
      
      // Find the user with the most contracts
      let maxCount = 0;
      let maxCountUserId = "";
      
      Object.entries(userContractCounts).forEach(([userId, count]) => {
        if (count > maxCount) {
          maxCount = count;
          maxCountUserId = userId;
        }
      });
      
      if (maxCountUserId) {
        userWithMostContracts = {
          name: `User ${maxCountUserId.substring(0, 8)}`,
          count: maxCount
        };
      }
    }
    
    return {
      data: { 
        totalContracts, 
        userWithMostContracts
      },
      error: contractsError || contractsDataError
    };
  },
  
  // Value stats for admin
  getAdminValueStats: async (): Promise<QueryResult> => {
    const { data: contracts, error: contractsError } = await supabase
      .from('contracts')
      .select('expected_cost');
    
    let totalContractValue = 0;
    if (!contractsError && contracts) {
      totalContractValue = contracts.reduce(
        (sum, contract) => sum + parseFloat(String(contract.expected_cost || 0)), 
        0
      );
    }
    
    return {
      data: { totalContractValue },
      error: contractsError
    };
  },
  
  // Email stats
  getEmailStats: async (): Promise<QueryResult> => {
    const { data, error } = await supabase.rpc('count_emails_by_status');
    
    const sentEmails = data?.find(item => item.status === 'sent');
    const totalSentEmails = sentEmails ? Number(sentEmails.count) : 0;
    
    return {
      data: { emailsSent: totalSentEmails, emailStats: data || [] },
      error
    };
  },
  
  // Get error distribution
  getErrorDistribution: async (): Promise<QueryResult> => {
    const { data, error } = await supabase.rpc('count_logs_by_level');
    
    return {
      data: { errorStats: data || [], totalErrorCount: data ? data.reduce((sum, item) => sum + Number(item.count), 0) : 0 },
      error
    };
  },
  
  // Get recent errors
  getRecentErrors: async (): Promise<QueryResult> => {
    const { data, error } = await supabase
      .from('system_logs')
      .select('id, level, message, location, created_at')
      .order('created_at', { ascending: false })
      .limit(5);
    
    return {
      data: { recentErrors: data || [] },
      error
    };
  },

  // Get user growth timeline
  getUserGrowthTimeline: async (): Promise<QueryResult> => {
    const { data: userRoles, error } = await supabase
      .from('user_roles')
      .select('created_at, user_id')
      .order('created_at', { ascending: true });
    
    if (error) {
      return { data: { timeline: [], newUsersWeek: 0, newUsersMonth: 0, newUsersYear: 0 }, error };
    }
    
    const now = new Date();
    const weekAgo = new Date(now); 
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const monthAgo = new Date(now);
    monthAgo.setDate(monthAgo.getDate() - 30);
    
    const yearAgo = new Date(now);
    yearAgo.setDate(yearAgo.getDate() - 365);
    
    const newUsersWeek = userRoles?.filter(user => new Date(user.created_at) >= weekAgo).length || 0;
    const newUsersMonth = userRoles?.filter(user => new Date(user.created_at) >= monthAgo).length || 0;
    const newUsersYear = userRoles?.filter(user => new Date(user.created_at) >= yearAgo).length || 0;
    
    // Create a timeline for the last 7 days
    const timeline = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const count = userRoles?.filter(user => {
        const userDate = new Date(user.created_at);
        return userDate.getDate() === date.getDate() && 
               userDate.getMonth() === date.getMonth() &&
               userDate.getFullYear() === date.getFullYear();
      }).length || 0;
      
      timeline.push({ date: dateStr, count });
    }
    
    return {
      data: { timeline, newUsersWeek, newUsersMonth, newUsersYear },
      error: null
    };
  },

  // Get notable users
  getNotableUsers: async (): Promise<QueryResult> => {
    // In a real implementation, this would query the database for users with most contracts and highest value
    const { data: contracts, error } = await supabase
      .from('contracts')
      .select('expected_cost, user_id');
    
    if (error) {
      return { 
        data: { 
          userWithMostContracts: { name: "Unknown", count: 0 },
          userWithHighestValue: { name: "Unknown", value: 0 }
        }, 
        error 
      };
    }
    
    const userContractCounts: Record<string, number> = {};
    const userContractValues: Record<string, number> = {};
    
    contracts.forEach(contract => {
      const cost = parseFloat(String(contract.expected_cost || 0));
      const userId = contract.user_id;
      
      userContractCounts[userId] = (userContractCounts[userId] || 0) + 1;
      userContractValues[userId] = (userContractValues[userId] || 0) + cost;
    });
    
    let maxContracts = 0;
    let userWithMostContractsId = "";
    let maxValue = 0;
    let userWithHighestValueId = "";
    
    Object.entries(userContractCounts).forEach(([userId, count]) => {
      if (count > maxContracts) {
        maxContracts = count;
        userWithMostContractsId = userId;
      }
    });
    
    Object.entries(userContractValues).forEach(([userId, value]) => {
      if (value > maxValue) {
        maxValue = value;
        userWithHighestValueId = userId;
      }
    });
    
    // In a real implementation, we would fetch user names from the auth.users table via an Edge Function
    
    return {
      data: { 
        userWithMostContracts: { name: userWithMostContractsId, count: maxContracts },
        userWithHighestValue: { name: userWithHighestValueId, value: maxValue }
      },
      error: null
    };
  },

  // Get weekly user growth data for current year
  getWeeklyUserGrowth: async (): Promise<QueryResult> => {
    try {
      // Get current year's start and end dates
      const currentYear = new Date().getFullYear();
      const startOfYear = new Date(currentYear, 0, 1).toISOString();
      const endOfYear = new Date(currentYear, 11, 31).toISOString();
      
      // Fetch all user registrations for the current year
      const { data: userRoles, error } = await supabase
        .from('user_roles')
        .select('created_at, user_id')
        .gte('created_at', startOfYear)
        .lte('created_at', endOfYear)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Initialize weeks array (1-52)
      const weeksData: { week: string, count: number }[] = [];
      for (let i = 1; i <= 52; i++) {
        weeksData.push({ week: `W${i}`, count: 0 });
      }
      
      // Process data to get counts by week
      const uniqueUserIds = new Set();
      let totalNewUsers = 0;
      
      userRoles.forEach(user => {
        // Skip duplicate user IDs (e.g., if a user has multiple roles)
        if (uniqueUserIds.has(user.user_id)) return;
        uniqueUserIds.add(user.user_id);
        totalNewUsers++;
        
        // Calculate the week number (1-52)
        const date = new Date(user.created_at);
        const startOfYear = new Date(date.getFullYear(), 0, 1);
        const weekNumber = Math.ceil((((date.getTime() - startOfYear.getTime()) / 86400000) + startOfYear.getDay() + 1) / 7);
        
        // Increment count for this week
        if (weekNumber >= 1 && weekNumber <= 52) {
          weeksData[weekNumber - 1].count++;
        }
      });
      
      // Return only weeks that have data or are before the current week
      const currentDate = new Date();
      const startOfCurrentYear = new Date(currentDate.getFullYear(), 0, 1);
      const currentWeekNumber = Math.ceil((((currentDate.getTime() - startOfCurrentYear.getTime()) / 86400000) + startOfCurrentYear.getDay() + 1) / 7);
      
      const relevantWeeksData = weeksData.slice(0, currentWeekNumber);
      
      return {
        data: { 
          weeklyUsersData: relevantWeeksData,
          totalNewUsers
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching weekly user growth data:', error);
      return {
        data: { weeklyUsersData: [], totalNewUsers: 0 },
        error
      };
    }
  },

  // Get active users
  getActiveUsers: async (): Promise<QueryResult> => {
    try {
      // In a real implementation, this would fetch the most active users based on login activity
      // Since we don't track login activity in this demo, we'll simulate it with a query on user_roles
      
      const { data: users, error } = await supabase
        .from('user_roles')
        .select('user_id, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (error) throw error;
      
      // Map to the expected format for the active users table
      const activeUsers = users.map(user => ({
        id: user.user_id,
        email: `user_${user.user_id.substring(0, 8)}@example.com`, // Simulated email
        lastActive: new Date(user.created_at).toISOString()
      }));
      
      return {
        data: { activeUsers },
        error: null
      };
    } catch (error) {
      console.error('Error fetching active users:', error);
      return {
        data: { activeUsers: [] },
        error
      };
    }
  }
};
